import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { app, db, functions } from "../../firebaseConfig";
import { doc, getDoc, collection, query, where, getDocs, Timestamp, limit, addDoc, updateDoc, writeBatch, getCountFromServer, DocumentData, documentId } from 'firebase/firestore';
import { useAppSelector } from '../../redux/store';
import Toast from '../Toast';

// Funtions
import getCurrentScreen from "../../Helper/GetScreen";
import assignByDepartment from '../../pages/Clients/Admin/Function/DepartmentFunctions';

interface TransferModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedStudent: {
        id: string;
        name: string;
        phone: string;
    };
}

const TransferModal = (
    { isOpen, onClose, selectedStudent }: TransferModalProps
) => {

    const navigate = useNavigate();

    const { userData, currentUser, isAdmin, isManager, isStaff } = useAuth();
    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<{ id: string, name: string }[]>([]);
    const [selectedTransferItem, setSelectedTransferItem] = useState<{ id: string, name: string } | null>(null);
    const [currentAssignedUser, setCurrentAssignedUser] = useState<{ id: string, name: string } | null>(null);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);
    const [departments, setDepartments] = useState<{ id: string, name: string, totalLeads: number }[]>([]);
    const [toUser, setToUser] = useState(true);
    const [userInDepartment, setUserInDepartment] = useState<{ id: string, name: string } | null>(null);

    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    // Fetch the current assigned user when the modal opens
    useEffect(() => {
        const fetchCurrentAssignedUser = async () => {
            if (!userData?.companyId || !selectedStudent.id) return;

            try {
                const leadRef = doc(db, `companies/${userData.companyId}/leads`, selectedStudent.id);
                const leadDoc = await getDoc(leadRef);
                const leadData = leadDoc.data();

                if (leadData && leadData.assignedTo) {
                    const userRef = doc(db, 'users', leadData.assignedTo);
                    const userDoc = await getDoc(userRef);
                    const userData = userDoc.data();

                    if (userData) {
                        setCurrentAssignedUser({ id: userDoc.id, name: userData.name });
                    }
                }
            } catch (error) {
                console.error('Error fetching current assigned user:', error);
            }
        };

        fetchCurrentAssignedUser();
    }, [isOpen, selectedStudent.id, userData?.companyId]);

    const handleSearchUsers = async (value: string) => {
        if (!userData?.companyId || value.length < 3) {
            setSearchResults([]);
            return;
        }

        setIsLoadingUsers(true);
        try {
            const upperValue = value.toUpperCase();
            const usersRef = collection(db, 'users');
            const q = query(
                usersRef,
                where('companyId', '==', userData.companyId),
                where('name', '>=', upperValue),
                where('name', '<=', upperValue + '\uf8ff'),
                limit(10)
            );

            const querySnapshot = await getDocs(q);
            const usersData = querySnapshot.docs
                .filter(doc => doc.data().isActive !== false) // Filter out deactivated users
                .map(doc => ({
                    id: doc.id,
                    name: doc.data().name
                }));

            setSearchResults(usersData);
        } catch (error) {
            console.error('Error searching users:', error);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const fetchDepartments = async () => {
        if (!userData?.companyId) return;
        setIsLoadingDepartments(true);
        try {
            const departmentsRef = collection(db, 'companies', userData.companyId, 'departments');
            const q = query(departmentsRef);
            const querySnapshot = await getDocs(q);
            const departmentsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().department,
                totalLeads: doc.data().totalLeadsAssigned || 0
            }));
            setDepartments(departmentsData);
        } catch (error) {
            console.error('Error fetching departments:', error);
        } finally {
            setIsLoadingDepartments(false);
        }
    };

    useEffect(() => {
        fetchDepartments();
    }, [userData?.companyId]);

    const handleSearchDepartments = async (value: string) => {
        if (!userData?.companyId || value.length < 3) {
            setSearchResults([]);
            return;
        }
        setIsLoadingDepartments(true);
        try {
            const upperValue = value;
            const departmentsRef = collection(db, 'companies', userData.companyId, 'departments');
            const q = query(
                departmentsRef,
                where('department', '>=', upperValue),
                where('department', '<=', upperValue + '\uf8ff'),
                limit(10)
            );
            const querySnapshot = await getDocs(q);
            const departmentsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().department
            }));
            setSearchResults(departmentsData);
        } catch (error) {
            console.error('Error searching departments:', error);
        } finally {
            setIsLoadingDepartments(false);
        }
    };

    const handleSelectTransferItem = (item: { id: string, name: string }) => {
        console.log('Selected Transfer Item:', item);
        setSelectedTransferItem(item);

        // if trasferring to a department, find the user which the lead is getting transferred to while with the round robin logic
        if (!toUser) {
            const findUserForDepartment = async (departmentId: string) => {
                if (!userData?.companyId) return;

                try {
                    const usersRef = collection(db, 'users');
                    const q = query(
                        usersRef,
                        where('companyId', '==', userData.companyId),
                        where('departmentId', '==', departmentId),
                        where('isActive', '==', true),
                    );
                    const querySnapshot = await getDocs(q);
                    const usersData = querySnapshot.docs.map(doc => ({
                        id: doc.id,
                        name: doc.data().name
                    }));
                    if (usersData.length === 0) {
                        setToastMessage('No active users found in this department.');
                        setShowToast(true);
                        setUserInDepartment(null);
                        return;
                    }

                    // Find the department data
                    const departmentData = departments.find(dept => dept.id === departmentId);
                    if (!departmentData) {
                        setToastMessage('Department not found.');
                        setShowToast(true);
                        return;
                    }

                    // Calculate the next user index in round robin style
                    const currentTotalLeadsAssigned = typeof departmentData.totalLeads === 'number' && !isNaN(departmentData.totalLeads)
                        ? departmentData.totalLeads
                        : 0;
                    const nextUserIndex = currentTotalLeadsAssigned % usersData.length;
                    console.log(`Next user index for department ${departmentId}:`, nextUserIndex);
                    const userToAssign = usersData[nextUserIndex];
                    console.log(`User to assign for department ${departmentId}:`, userToAssign);
                    if (userToAssign) {
                        setUserInDepartment(userToAssign);
                    }
                } catch (error) {
                    console.error('Error finding user for department:', error);
                    setToastMessage('Failed to find user for department. Please try again.');
                    setShowToast(true);
                }
            };
            findUserForDepartment(item.id);
        }
    };

    const handleTransferSubmit = async () => {
        if (!selectedStudent || !userData?.companyId || !selectedTransferItem) return;

        try {

            if (toUser) {
                // Find the lead document of this specific student
                const leadRef = doc(db, `companies/${userData.companyId}/leads`, selectedStudent.id);

                // get the activeFollowUpId of this specific student
                const leadDoc = await getDoc(leadRef);
                const leadData = leadDoc?.data();
                const activeFollowUpId = leadData?.activeFollowUpId;



                // if activeFollowUpId is present, find the followUp document of this specific student
                const followUpRef = activeFollowUpId ? doc(db, `companies/${userData.companyId}/followUps`, activeFollowUpId) : null;


                // Update assignedTo field of this specific student
                await updateDoc(leadRef, {
                    assignedTo: selectedTransferItem.id,
                    transferredAt: Timestamp.now(),
                    updatedBy: currentUser?.uid,
                });

                // if activeFollowUpId is present, update userId field of this specific student's followUp document
                if (followUpRef) {
                    await updateDoc(followUpRef, {
                        userId: selectedTransferItem.id
                    });
                }

                setToastMessage('Lead transferred successfully!');
                setShowToast(true);
            } else {
                // If transferring to a department, use the assignByDepartment function
                const response = await assignByDepartment(
                    currentUser?.uid || '',
                    userData.companyId,
                    selectedTransferItem.id,
                    selectedStudent.id
                );
                if (!response || !response.message) {
                    throw new Error('Failed to transfer lead to department');
                }
                setToastMessage(response.message);
                setShowToast(true);
            }
            setSelectedTransferItem(null);
            setSearchTerm('');
            setSearchResults([]);
            onClose();
            navigate('/leadmanagement');
        } catch (error) {
            setToastMessage('Failed to transfer lead. Please try again.');
            setShowToast(true);
            console.error('Error transferring lead:', error);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            {isOpen && (
                <div className="bg-white rounded-3xl w-[500px] max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-medium">Transfer Lead</h3>
                            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="mb-6">
                            <p className="text-sm text-gray-600 mb-1">Student Name</p>
                            <p className="font-medium">{selectedStudent.name}</p>
                            <p className="text-sm text-gray-600 mt-3 mb-1">Phone Number</p>
                            <p className="font-medium">{selectedStudent.phone}</p>
                            {isAdmin && currentScreen === "Admin" && (
                                <>
                                    <p className="text-sm text-gray-600 mt-3 mb-1">Current Relationship Manager</p>
                                    <p className="font-medium">{currentAssignedUser?.name || 'Not assigned'}</p>
                                </>
                            )}
                        </div>

                        {/* check box to select users or departments */}
                        <div className="mb-6 flex space-x-3">
                            <label className={`px-4 py-2 rounded-full flex items-center gap-2 text-slate-500 transition-all 
                            ${toUser ? 'shadow-[inset_-2px_-2px_5px_rgba(255,255,255,1),inset_2px_2px_4px_rgba(0,0,0,0.3),-1px_-1px_5px_rgba(255,255,255,0.6),1px_1px_5px_rgba(0,0,0,0.3)] text-violet-500' : 'shadow-[-5px_-5px_10px_rgba(255,255,255,0.8),5px_5px_10px_rgba(0,0,0,0.25)]'
                                }`}
                            >
                                <input type="checkbox" className="peer hidden" checked={toUser} onChange={() => { setToUser(true); setSearchTerm('') }} />
                                <span className="text-sm font-medium">To User</span>
                            </label>

                            <label className={`px-4 py-2 rounded-full flex items-center gap-2 text-slate-500 transition-all 
                            ${!toUser ? 'shadow-[inset_-2px_-2px_5px_rgba(255,255,255,1),inset_2px_2px_4px_rgba(0,0,0,0.3),-1px_-1px_5px_rgba(255,255,255,0.6),1px_1px_5px_rgba(0,0,0,0.3)] text-violet-500' : 'shadow-[-5px_-5px_10px_rgba(255,255,255,0.8),5px_5px_10px_rgba(0,0,0,0.25)]'
                                }`}
                            >
                                <input type="checkbox" className="peer hidden" checked={!toUser} onChange={() => { setToUser(false); setSearchResults([]); }} />
                                <span className="text-sm font-medium">To Department</span>
                            </label>
                        </div>



                        {toUser ? (
                            <div className="mb-6">
                                <label className="block text-sm text-gray-600 mb-2">Search Users</label>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        handleSearchUsers(e.target.value);
                                    }}
                                    placeholder="Type to search users..."
                                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                />
                            </div>
                        ) : (
                            <div className="mb-6">
                                <label className="block text-sm text-gray-600 mb-2">Select Departments</label>
                                <select
                                    value={selectedTransferItem?.id || ''}
                                    onChange={(e) => {
                                        handleSelectTransferItem({ id: e.target.value, name: departments?.find(dept => dept.id === e.target.value)?.name || '' });
                                    }}
                                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                >
                                    <option value="">Select Department</option>
                                    {departments.map(department => (
                                        <option key={department.id} value={department.id}>
                                            {department.name}
                                        </option>
                                    ))}
                                </select>
                                {userInDepartment && (
                                    <p className="text-sm text-gray-600 mt-2 whitespace-nowrap">Next user in department: <span className="bg-gray-100 px-3 py-1 w-fit rounded-full text-sm text-gray-800" >{userInDepartment.name} </span>
                                    </p>
                                )}
                                {/* {userInDepartment && (
                                <div className="flex items-center gap-2 bg-gray-100 px-3 py-1 w-fit rounded-full text-sm text-gray-800" >
                                    {userInDepartment?.name}
                                </div>
                                )} */}
                            </div>
                        )}

                        {(isLoadingUsers) ? (
                            <div className="text-center py-4">Loading...</div>
                        ) : (
                            <div className="max-h-48 overflow-y-auto mb-6">
                                {searchResults.map(item => (
                                    <div
                                        key={item.id}
                                        onClick={() => handleSelectTransferItem(item)}
                                        className={`p-3 rounded-lg cursor-pointer mb-2 ${selectedTransferItem?.id === item.id
                                            ? 'bg-cyan-100 border-2 border-cyan-500'
                                            : 'hover:bg-gray-100'
                                            }`}
                                    >
                                        {item.name}
                                    </div>
                                ))}
                            </div>
                        )}


                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={onClose}
                                className="px-6 py-2 text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleTransferSubmit}
                                disabled={!selectedTransferItem}
                                className={`px-6 py-2 text-white rounded-full ${selectedTransferItem
                                    ? 'bg-cyan-600 hover:bg-cyan-700'
                                    : 'bg-gray-400 cursor-not-allowed'
                                    }`}
                            >
                                Transfer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={
                        toastMessage.includes('Error') ||
                        toastMessage.includes('Cannot') ||
                        toastMessage.includes('Failed')
                    }
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div>
    )
}

export default TransferModal
